import React, { useState } from "react";
import {
  ArrowRight,
  Clock,
  HelpCircle,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Send,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { getErrorMessage } from "../../core/api.jsx";
import { createSupportTicket } from "../../core/services/supportService";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";

export default function ContactUs() {
  const { settings } = useOutletContext();
  const { isAuthenticated, user } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();

  const readContactDraft = () => {
    try {
      const draft = sessionStorage.getItem("contactSupportDraft");
      return draft ? JSON.parse(draft) : null;
    } catch {
      return null;
    }
  };

  const [form, setForm] = useState(() => readContactDraft() || ({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    category: "order",
    subject: "",
    message: "",
  }));
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const supportEmail = settings?.general?.supportEmail || "hello@sugamstore.in";
  const supportPhone = settings?.general?.supportPhone || "+91 98765 43210";
  const whatsappNumber = settings?.general?.whatsappNumber || supportPhone;
  const storeAddress = settings?.general?.address || "Sugam Store, Express Logistics Hub, Sector 18, Gurugram, India";

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.message.trim() || !form.subject.trim()) {
      return setError("Please fill in all required fields.");
    }

    setBusy(true);
    setError("");

    try {
      if (!isAuthenticated) {
        sessionStorage.setItem("contactSupportDraft", JSON.stringify(form));
        navigate("/login", { state: { from: "/contact" } });
        return;
      }

      await createSupportTicket({
        subject: form.subject.trim(),
        message: form.message.trim(),
        category: form.category,
      });
      sessionStorage.removeItem("contactSupportDraft");
      setSubmitted(true);
      notify("Message sent successfully! Our team will respond shortly.");
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Could not send message. Please contact us via email or phone."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="page-shell content-page contact-page">
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Contact Us</span>
      </div>

      <div className="page-title-row">
        <div>
          <p className="eyebrow">
            <Sparkles size={14} /> WE'RE HERE TO HELP
          </p>
          <h1>Contact Customer Support</h1>
        </div>
        <span>Response within 2-4 hours</span>
      </div>

      <div className="contact-layout">
        {/* Contact Info Channels */}
        <section className="contact-channels">
          <div className="contact-channel-card">
            <div className="channel-icon">
              <Mail size={22} />
            </div>
            <div>
              <h3>Email Assistance</h3>
              <p>Send your queries or order feedback to our dedicated support inbox.</p>
              <a href={`mailto:${supportEmail}`} className="contact-channel-link">
                {supportEmail} <ArrowRight size={14} />
              </a>
            </div>
          </div>

          <div className="contact-channel-card">
            <div className="channel-icon">
              <Phone size={22} />
            </div>
            <div>
              <h3>Phone Helpline</h3>
              <p>Speak directly with our friendly customer representative.</p>
              <a href={`tel:${supportPhone}`} className="contact-channel-link">
                {supportPhone} <ArrowRight size={14} />
              </a>
            </div>
          </div>

          <div className="contact-channel-card">
            <div className="channel-icon">
              <MessageSquare size={22} />
            </div>
            <div>
              <h3>WhatsApp Chat</h3>
              <p>Fast answers on product availability, delivery tracking, and returns.</p>
              <a
                href={`https://wa.me/${whatsappNumber.replace(/\D/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="contact-channel-link"
              >
                Chat on WhatsApp <ArrowRight size={14} />
              </a>
            </div>
          </div>

          <div className="contact-channel-card">
            <div className="channel-icon">
              <MapPin size={22} />
            </div>
            <div>
              <h3>Registered Hub</h3>
              <p>{storeAddress}</p>
              <span className="channel-badge">
                <Clock size={13} /> Mon - Sat: 9:00 AM - 8:00 PM IST
              </span>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="contact-form-card">
          {submitted ? (
            <div className="contact-success-state">
              <div className="success-icon">
                <ShieldCheck size={36} />
              </div>
              <h2>Thank You for Reaching Out!</h2>
              <p>
                We have received your message. One of our support executives will review your query 
                and get back to you at <strong>{form.email || form.phone || "your contact details"}</strong> within a few hours.
              </p>
              <button
                className="button button--primary"
                onClick={() => {
                  setSubmitted(false);
                  setForm({
                    name: user?.name || "",
                    email: user?.email || "",
                    phone: user?.phone || "",
                    category: "order",
                    subject: "",
                    message: "",
                  });
                }}
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="form-stack">
              <h2>Send Us a Message</h2>
              <p className="form-subtitle">
                Fill in the details below and we will get back to you as soon as possible.
              </p>

              {error && <div className="inline-alert">{error}</div>}

              <div className="form-row-2">
                <div className="field">
                  <label htmlFor="contact-name">Your Full Name *</label>
                  <input
                    id="contact-name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="e.g. Rahul Sharma"
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="contact-phone">Mobile Number</label>
                  <input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="e.g. 9876543210"
                  />
                </div>
              </div>

              <div className="form-row-2">
                <div className="field">
                  <label htmlFor="contact-email">Email Address</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="name@example.com"
                  />
                </div>
                <div className="field">
                  <label htmlFor="contact-category">Inquiry Topic</label>
                  <select
                    id="contact-category"
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    className="select-field"
                  >
                    <option value="order">Order Tracking & Status</option>
                    <option value="payment">Payment & Refunds</option>
                    <option value="product">Product Details & Stock</option>
                    <option value="account">Account & Login</option>
                    <option value="other">General Feedback / Other</option>
                  </select>
                </div>
              </div>

              <div className="field">
                <label htmlFor="contact-subject">Subject *</label>
                <input
                  id="contact-subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="Brief summary of your inquiry"
                  required
                />
              </div>

              <div className="field">
                <label htmlFor="contact-message">Message *</label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="How can we assist you today? Please include order number if applicable."
                  required
                />
              </div>

              <button className="button button--primary button--full" disabled={busy}>
                {busy ? "Sending Message..." : (
                  <>
                    Submit Message <Send size={16} />
                  </>
                )}
              </button>
            </form>
          )}
        </section>
      </div>

      <section className="contact-quick-faq">
        <HelpCircle size={24} />
        <div>
          <h3>Have questions about shipping or returns?</h3>
          <p>Find immediate answers in our comprehensive Help Center and FAQ directory.</p>
        </div>
        <Link to="/faq" className="button button--quiet">
          Browse FAQs <ArrowRight size={16} />
        </Link>
      </section>
    </main>
  );
}
